import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { GetTicketsDocument } from "#graphql/supergraphHooks";
import {
  type GetTicketsQuery,
  type TicketFilters,
  type TicketSortInput,
  type AdditionalFilter,
  TicketStatus,
} from "#graphql/supergraphTypes";
import { toISOFormat } from "#utils/datetime";
import {
  JIRA_PROJECT_KEY,
  TICKETS_TABLE_PAGE_SIZE,
  UNASSIGNED_FILTER_VALUE,
} from "../constants";
import { BUILT_IN_JIRA_FIELDS } from "./constants/fieldMetadata";
import {
  TicketRowItem,
  BooleanFilter,
  SetFilter,
  TextFilter,
  DateFilter,
  TicketsData,
  FilterOptionsMap,
  isFilterOptionsWithMapping,
  type FieldMetadataMap,
} from "./types";

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const isTextFilter = (filter: unknown): filter is TextFilter => {
  return (
    isObject(filter) &&
    filter.filterType === "text" &&
    "filter" in filter &&
    typeof filter.filter === "string"
  );
};

const isSetFilter = (filter: unknown): filter is SetFilter => {
  return (
    isObject(filter) &&
    filter.filterType === "set" &&
    "values" in filter &&
    Array.isArray(filter.values)
  );
};

const isDateFilter = (filter: unknown): filter is DateFilter => {
  return isObject(filter) && filter.filterType === "date";
};

const fieldNameMapping: Record<string, string> = {
  workType: "issuetype",
  entityName: "entity_name",
  assignedTo: "assignee",
  faPreparer: "fa_preparer",
  faReviewer: "fa_reviewer",
  isPrimary: "is_primary",
  isReviewer: "is_reviewer",
  customers: "customers",
  status: "status",
  statusCategoryName: "statusCategory",
  requestedBy: "requested_by",
  key: "key",
  createdAt: "created",
  dueDate: "duedate",
};

const mapFilterTypeToJQLOperator = (filterType: string): string => {
  const operatorMap: Record<string, string> = {
    contains: "~",
    notContains: "!~",
    equals: "=",
    notEqual: "!=",
    startsWith: "~",
    endsWith: "~",
  };
  return operatorMap[filterType] || "~";
};

const fieldToFilterOptionsKey: Record<string, string> = {
  assignedTo: "assignee",
  faPreparer: "faPreparer",
  faReviewer: "faReviewer",
  isPrimary: "isPrimary",
  isReviewer: "isReviewer",
};

const translateDisplayToValue = (
  displayValue: string,
  field: string,
  filterOptionsMap?: FilterOptionsMap,
): string => {
  if (!filterOptionsMap) return displayValue;

  const filterOptionsKey = fieldToFilterOptionsKey[field];
  if (!filterOptionsKey) return displayValue;

  const filterOption = filterOptionsMap[filterOptionsKey];
  if (isFilterOptionsWithMapping(filterOption)) {
    return filterOption.displayToValueMap[displayValue] ?? displayValue;
  }
  return displayValue;
};

const formatValueForJQLSearch = (value: string, filterType: string): string => {
  if (filterType === "contains") {
    return `${value}*`;
  }

  return value;
};

export const getFilterVars = (
  filterModel:
    | AgGrid.FilterModel
    | AgGrid.AdvancedFilterModel
    | Record<string, SetFilter | BooleanFilter | TextFilter | DateFilter | undefined>
    | null,
  fieldMetadataMap?: FieldMetadataMap,
  filterOptionsMap?: FilterOptionsMap,
): TicketFilters => {
  const filters: TicketFilters = {
    projectKeys: [JIRA_PROJECT_KEY],
  };
  const additionalFilters: AdditionalFilter[] = [];

  if (!filterModel) {
    return {
      ...filters,
      statuses: ["IN_PROGRESS"],
      pastDueOnly: false,
    };
  }

  if (filterModel?.filterType) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }

  const getJiraFieldId = (fieldName: string): string | null => {
    const metadataFieldName = fieldNameMapping[fieldName] || fieldName;

    if (fieldMetadataMap && fieldMetadataMap.size > 0) {
      const fieldId = fieldMetadataMap.get(metadataFieldName);
      if (fieldId) {
        return fieldId;
      }
    }

    return metadataFieldName;
  };

  Object.entries(filterModel).forEach(([field, filter]) => {
    if (!filter) return;

    switch (field) {
      case "key":
        if (isTextFilter(filter) && filter.filter) {
          additionalFilters.push({
            field: "key",
            operator: mapFilterTypeToJQLOperator(filter.type),
            value: formatValueForJQLSearch(filter.filter, filter.type),
          });
        }
        break;
      case "description":
        if (isTextFilter(filter) && filter.filter) {
          additionalFilters.push({
            field: "summary",
            operator: mapFilterTypeToJQLOperator(filter.type),
            value: formatValueForJQLSearch(filter.filter, filter.type),
          });
        }
        break;
      case "requestedBy":
        if (isTextFilter(filter) && filter.filter) {
          additionalFilters.push({
            field: "Requested By",
            operator: mapFilterTypeToJQLOperator(filter.type),
            value: formatValueForJQLSearch(filter.filter, filter.type),
          });
        }
        break;
      case "customers":
        if (isSetFilter(filter) && filter.values.length > 0) {
          const jiraFieldId = getJiraFieldId("customers");
          if (jiraFieldId) {
            const hasUnassigned = filter.values.some(
              (v) => String(v) === UNASSIGNED_FILTER_VALUE,
            );
            const regularValues = filter.values
              .filter((v) => String(v) !== UNASSIGNED_FILTER_VALUE)
              .map(String);

            if (hasUnassigned) {
              additionalFilters.push({
                field: jiraFieldId,
                operator: "IS",
                value: "EMPTY",
              });
            }
            if (regularValues.length > 0) {
              additionalFilters.push({
                field: jiraFieldId,
                operator: "IN",
                values: regularValues,
              });
            }
          }
        }
        break;
      case "workType":
        if (isSetFilter(filter) && filter.values.length > 0) {
          const jiraFieldId = getJiraFieldId(field);
          if (jiraFieldId) {
            const translatedValues = filter.values.map((v) =>
              translateDisplayToValue(String(v), field, filterOptionsMap),
            );
            if (translatedValues.length === 1) {
              additionalFilters.push({
                field: jiraFieldId,
                operator: "=",
                value: translatedValues[0],
              });
            } else {
              additionalFilters.push({
                field: jiraFieldId,
                operator: "IN",
                values: translatedValues,
              });
            }
          }
        }
        break;
      case "entityName":
      case "assignedTo":
      case "faPreparer":
      case "faReviewer":
      case "isPrimary":
      case "isReviewer":
      case "status":
      case "statusCategoryName":
        if (isSetFilter(filter) && filter.values.length > 0) {
          const jiraFieldId = getJiraFieldId(field);
          if (jiraFieldId) {
            const hasUnassigned = filter.values.some(
              (v) => String(v) === UNASSIGNED_FILTER_VALUE,
            );
            const regularValues = filter.values.filter(
              (v) => String(v) !== UNASSIGNED_FILTER_VALUE,
            );
            const translatedValues = regularValues.map((v) =>
              translateDisplayToValue(String(v), field, filterOptionsMap),
            );

            if (hasUnassigned) {
              additionalFilters.push({
                field: jiraFieldId,
                operator: "IS",
                value: "EMPTY",
              });
            }
            if (translatedValues.length === 1) {
              additionalFilters.push({
                field: jiraFieldId,
                operator: "=",
                value: translatedValues[0],
              });
            } else if (translatedValues.length > 1) {
              additionalFilters.push({
                field: jiraFieldId,
                operator: "IN",
                values: translatedValues,
              });
            }
          }
        }
        break;
      case "dueDate":
        if (isDateFilter(filter)) {
          const jiraFieldId = getJiraFieldId(field);
          if (jiraFieldId) {
            if (filter.type === "inRange") {
              if (filter.dateFrom) {
                additionalFilters.push({
                  field: jiraFieldId,
                  operator: ">=",
                  value: toISOFormat(filter.dateFrom),
                });
              }
              if (filter.dateTo) {
                additionalFilters.push({
                  field: jiraFieldId,
                  operator: "<=",
                  value: toISOFormat(filter.dateTo),
                });
              }
            } else if (filter.dateFrom) {
              let operator: string | undefined;
              if (filter.type === "equals") {
                operator = "=";
              } else if (filter.type === "greaterThan") {
                operator = ">";
              } else if (filter.type === "greaterThanOrEqual") {
                operator = ">=";
              } else if (filter.type === "lessThan") {
                operator = "<";
              } else if (filter.type === "lessThanOrEqual") {
                operator = "<=";
              }
              if (operator) {
                additionalFilters.push({
                  field: jiraFieldId,
                  operator,
                  value: toISOFormat(filter.dateFrom),
                });
              }
            }
          }
        }
        break;

      default:
        break;
    }
  });

  if (additionalFilters.length > 0) {
    filters.additionalFilters = additionalFilters;
  }

  return filters;
};

export const getSortVars = (
  sortModel: AgGrid.SortModelItem[] | null,
  fieldMetadataMap?: FieldMetadataMap,
): TicketSortInput | null => {
  if (!sortModel || sortModel.length === 0) {
    return null;
  }

  const firstSort = sortModel[0];
  const metadataFieldName = fieldNameMapping[firstSort.colId] || firstSort.colId;
  const backendField = fieldMetadataMap?.get(metadataFieldName) ?? metadataFieldName;

  return {
    field: backendField,
    direction: firstSort.sort === "asc" ? "ASC" : "DESC",
  };
};

export const rowsFromQuery = (tickets: TicketsData): TicketRowItem[] => {
  const rowData = tickets.map((ticket) => ({
    id: ticket.id,
    key: ticket.key,
    description: ticket.description,
    ticketType: ticket.ticketType,
    workType: ticket.workType,
    status: ticket.status as TicketStatus,
    statusCategoryName: ticket.statusCategoryName || null,
    customers: ticket.customers || [],
    entityName: ticket.entityName,
    createdAt: String(ticket.createdAt),
    updated: ticket.updated || null,
    jiraLink: ticket.jiraLink,
    isPrimary: ticket.isPrimary || null,
    isReviewer: ticket.isReviewer || null,
    faPreparer: ticket.faPreparer || null,
    faReviewer: ticket.faReviewer || null,
    requestedBy: ticket.requestedBy || null,
    assignedTo: ticket.assignedTo || null,
    client: ticket.client || null,
    manager: ticket.manager || null,
    managerOfAssignedTo: ticket.managerOfAssignedTo || null,
    progress: ticket.progress,
    pastDue: ticket.pastDue,
    timeElapsed: ticket.timeElapsed,
    dueDate: ticket.dueDate || null,
  }));
  return rowData;
};

export type TicketsGraphqlDataSourceProps = {
  onSortsChange?: (sorts: TicketSortInput | null) => void;
  filterOptionsMap?: FilterOptionsMap;
  fieldMetadataMap?: Map<string, string>;
  excludeAllCompleted?: boolean;
};

export class TicketsGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  private onSortsChange?: (sorts: TicketSortInput | null) => void;

  private pageTokens: Map<number, string | null> = new Map();
  private pageSize: number = TICKETS_TABLE_PAGE_SIZE;
  private currentFilters: TicketFilters | null = null;
  private currentSorts: TicketSortInput | null = null;
  private fieldMetadataMap: FieldMetadataMap = new Map();
  private filterOptionsMap?: FilterOptionsMap;
  private requestId: number = 0;
  private excludeAllCompleted: boolean = false;

  constructor(
    props: TicketsGraphqlDataSourceProps | undefined,
    apolloClient: ApolloClient<NormalizedCacheObject>,
  ) {
    this.client = apolloClient;
    this.onSortsChange = props?.onSortsChange;
    this.filterOptionsMap = props?.filterOptionsMap;
    this.excludeAllCompleted = props?.excludeAllCompleted ?? false;

    if (props?.fieldMetadataMap) {
      this.fieldMetadataMap = props.fieldMetadataMap;
    }

    this.pageTokens.set(0, null);
  }

  setExcludeAllCompleted(excludeAllCompleted: boolean): void {
    if (this.excludeAllCompleted !== excludeAllCompleted) {
      this.clearPageTokens();
      this.excludeAllCompleted = excludeAllCompleted;
    }
  }

  setFilterOptionsMap(filterOptionsMap: FilterOptionsMap): void {
    this.filterOptionsMap = filterOptionsMap;
  }

  private getPageNumber(startRow: number): number {
    return Math.floor(startRow / this.pageSize);
  }

  private getPageToken(pageNumber: number): string | null {
    const token = this.pageTokens.get(pageNumber);
    return token ?? null;
  }

  private storeNextPageToken(currentPageNumber: number, nextToken: string | null): void {
    if (nextToken) {
      this.pageTokens.set(currentPageNumber + 1, nextToken);
    }
  }

  private clearPageTokens(): void {
    this.pageTokens.clear();
    this.pageTokens.set(0, null);
  }

  public getFieldMetadataMap(): FieldMetadataMap {
    return this.fieldMetadataMap;
  }

  public handleError(params: AgGrid.IServerSideGetRowsParams): void {
    params.fail();
  }

  public handleSuccess(
    params: AgGrid.IServerSideGetRowsParams,
    response: GetTicketsQuery,
    requestId: number,
  ): void {
    if (requestId < this.requestId) {
      return;
    }

    if (!response.tickets) {
      this.handleError(params);
      return;
    }

    const { tickets, hasNextPage, nextPageToken, fieldMetadata } = response.tickets;

    if (fieldMetadata) {
      fieldMetadata.forEach((metadata) => {
        const fieldId = BUILT_IN_JIRA_FIELDS.has(metadata.fieldName)
          ? metadata.fieldName
          : metadata.jiraFieldId;
        this.fieldMetadataMap.set(metadata.fieldName, fieldId);
      });
    }

    const rowData = rowsFromQuery(tickets);

    const startRow = params.request.startRow ?? 0;
    const currentPageNumber = this.getPageNumber(startRow);

    this.storeNextPageToken(currentPageNumber, nextPageToken);

    const canFetchMorePages = hasNextPage && nextPageToken != null;
    const rowCount = canFetchMorePages ? undefined : startRow + rowData.length;

    params.success({
      rowData,
      rowCount,
    });

    if (rowData.length === 0) {
      params.api?.showNoRowsOverlay();
    } else {
      params.api?.hideOverlay();
    }
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? 20;
    const limit = endRow - startRow;
    const currentPageNumber = this.getPageNumber(startRow);

    const filters = getFilterVars(
      params.request.filterModel,
      this.fieldMetadataMap.size > 0 ? this.fieldMetadataMap : undefined,
      this.filterOptionsMap,
    );

    const sorts = getSortVars(
      params.request.sortModel,
      this.fieldMetadataMap.size > 0 ? this.fieldMetadataMap : undefined,
    ) || {
      field: "created_at",
      direction: "DESC",
    };

    const filtersChanged = JSON.stringify(this.currentFilters) !== JSON.stringify(filters);
    const sortsChanged = JSON.stringify(this.currentSorts) !== JSON.stringify(sorts);

    if (filtersChanged || sortsChanged) {
      this.clearPageTokens();
      this.currentFilters = filters;
      this.currentSorts = sorts;
    }

    this.requestId++;
    const currentRequestId = this.requestId;

    const pageToken = this.getPageToken(currentPageNumber);

    if (this.onSortsChange && sortsChanged) {
      this.onSortsChange(sorts);
    }

    this.client
      .query<GetTicketsQuery>({
        query: GetTicketsDocument,
        variables: {
          filters: filters,
          sort: sorts,
          limit: limit,
          pageToken: pageToken,
          excludeAllCompleted: this.excludeAllCompleted,
        },

        fetchPolicy: "no-cache",
      })
      .then((response) => {
        this.handleSuccess(params, response.data, currentRequestId);
      })
      .catch(() => {
        this.handleError(params);
      });
  }
}
